import { useState, useEffect } from 'react'
import {
  Router,
  Settings,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Sparkles,
  Eye,
  EyeOff,
  Bot,
  Zap,
  Calendar,
  RefreshCw,
  Shield,
} from 'lucide-react'
import {
  getRouterConfig,
  updateRouterConfig,
  getAgents,
  bulkReclassify,
  getBulkReclassifyProgress,
  type RouterConfig,
  type CustomAgent,
  type BulkReclassifyProgress,
} from '@/services/api'
import { useRole } from '@/hooks/useRole'
import ConfirmModal from '@/components/ConfirmModal'
import {
  AI_PROVIDERS,
  PROVIDER_STYLES,
  REASONING_STYLES,
  getProviderFromModel,
  getModelInfo,
  getProviderInfo,
  getReasoningLevelsForProvider,
  getDefaultReasoningForProvider,
} from '@/config/models'

// Module-level cache
let _cachedRouterConfig: RouterConfig | null = null
let _cachedRouterAgents: CustomAgent[] | null = null

export default function AIRouterPage() {
  const [config, setConfig] = useState<RouterConfig>(_cachedRouterConfig || {
    enabled: true,
    model: 'gpt-5-mini',
    reasoning_effort: 'low',
    system_prompt: undefined,
    fallback_agent_id: undefined,
    auto_classify_enabled: false,
    auto_classify_since: undefined,
  })
  const [agents, setAgents] = useState<CustomAgent[]>(_cachedRouterAgents || [])
  const [loading, setLoading] = useState(!_cachedRouterConfig)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<RouterConfig>({ ...config })
  const [showPromptPreview, setShowPromptPreview] = useState(false)

  // Bulk re-classify state (super admin only)
  const { isSuperAdmin } = useRole()
  const [bulkSinceDate, setBulkSinceDate] = useState('')
  const [bulkForce, setBulkForce] = useState(true)
  const [bulkRunning, setBulkRunning] = useState(false)
  const [showBulkConfirm, setShowBulkConfirm] = useState(false)
  const [bulkProgress, setBulkProgress] = useState<BulkReclassifyProgress | null>(null)
  const [bulkResults, setBulkResults] = useState<{
    total_found: number
    reclassified: number
    skipped: number
    errors: number
    error_details: string[]
  } | null>(null)

  const handleBulkReclassify = async () => {
    setShowBulkConfirm(false)
    setBulkRunning(true)
    setBulkResults(null)
    setBulkProgress(null)
    try {
      await bulkReclassify({
        since_date: bulkSinceDate || undefined,
        force: bulkForce,
        include_priority: false,
      })
      // Polling will handle the rest via useEffect below
    } catch (err: any) {
      setBulkResults({
        total_found: 0,
        reclassified: 0,
        skipped: 0,
        errors: 1,
        error_details: [err.message || 'Unknown error'],
      })
      setBulkRunning(false)
    }
  }

  // Poll bulk reclassify progress
  useEffect(() => {
    if (!bulkRunning) return

    const pollProgress = async () => {
      try {
        const data = await getBulkReclassifyProgress()
        setBulkProgress(data)

        if (data.status === 'completed' || data.status === 'error') {
          setBulkRunning(false)
          setBulkResults({
            total_found: data.total_found,
            reclassified: data.reclassified,
            skipped: data.skipped,
            errors: data.errors,
            error_details: data.error_details || [],
          })
        }
      } catch (err) {
        console.error('Error polling bulk reclassify progress:', err)
      }
    }

    pollProgress()
    const interval = setInterval(pollProgress, 2000)
    return () => clearInterval(interval)
  }, [bulkRunning])

  useEffect(() => {
    loadConfig()
    loadAgents()
  }, [])

  const loadConfig = async () => {
    try {
      if (!_cachedRouterConfig) setLoading(true)
      setError(null)
      const data = await getRouterConfig()
      setConfig(data)
      setFormData(data)
      _cachedRouterConfig = data
    } catch (err: any) {
      setError(err.message || 'Failed to load router configuration')
    } finally {
      setLoading(false)
    }
  }

  const loadAgents = async () => {
    try {
      const response = await getAgents()
      const newAgents = response.agents || []
      setAgents(newAgents)
      _cachedRouterAgents = newAgents
    } catch (err: any) {
      console.error('Failed to load agents:', err)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      await updateRouterConfig(formData)
      setConfig(formData)
      setIsEditing(false)
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData({ ...config })
    setIsEditing(false)
    setError(null)
  }

  const generatePromptPreview = () => {
    const activeAgents = agents.filter(a => a.is_active)

    if (activeAgents.length === 0) {
      return "No active agents available. Please create agents first."
    }

    const agentList = activeAgents
      .map(agent => `- ${agent.name}: ${agent.description || 'No description'}`)
      .join('\n')

    const agentNames = activeAgents.map(a => `'${a.name}'`).join(', ')

    return formData.system_prompt || `You are the Main Router. Classify the user's email into exactly one of these agents:
${agentList}

CRITICAL RULES:
1. IGNORE the Subject Line if it is empty or "No Subject". Focus 100% on the BODY.
2. SPAM CHECK: If the body sounds like a B2B sales pitch ("boost sales", "commission", "partnership"), return 'SPAM'.
3. Match the email content to the agent that best handles this type of inquiry based on their description.
4. If no agent matches, return 'UNKNOWN'.

Output ONLY the agent name (one of: ${agentNames}, 'SPAM', or 'UNKNOWN').`
  }

  if (loading && !_cachedRouterConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Router className="w-8 h-8 mr-3 text-blue-500" />
            AI Router
          </h1>
          <p className="text-gray-600 mt-2">
            Automatically classify incoming emails and route them to the appropriate AI agent
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-600">{error}</span>
        </div>
      )}

      {/* Configuration Card */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Settings className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Router Configuration</h2>
          </div>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Configure</span>
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg hover:bg-yellow-400 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* Enabled Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${formData.enabled ? 'bg-green-100' : 'bg-gray-200'}`}>
                <Sparkles className={`w-5 h-5 ${formData.enabled ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-medium text-gray-900">Router Status</p>
                <p className="text-sm text-gray-500">
                  {formData.enabled ? 'Router is active and classifying emails' : 'Router is disabled'}
                </p>
              </div>
            </div>
            {isEditing && (
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
              </label>
            )}
            {!isEditing && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                formData.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {formData.enabled ? 'Enabled' : 'Disabled'}
              </span>
            )}
          </div>

          {/* Auto-Classification Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-lg ${formData.auto_classify_enabled ? 'bg-yellow-100' : 'bg-gray-200'}`}>
                <Zap className={`w-5 h-5 ${formData.auto_classify_enabled ? 'text-yellow-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-medium text-gray-900">Auto-Classification</p>
                <p className="text-sm text-gray-500">
                  {formData.auto_classify_enabled
                    ? 'New emails are automatically classified by AI agents'
                    : 'Manual classification only - click "Classify" on each email'}
                </p>
                {formData.auto_classify_enabled && formData.auto_classify_since && (
                  <div className="flex items-center mt-2 text-xs text-gray-600">
                    <Calendar className="w-3 h-3 mr-1" />
                    Active since: {new Date(formData.auto_classify_since).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
            {isEditing && (
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.auto_classify_enabled || false}
                  onChange={(e) => {
                    const enabled = e.target.checked
                    setFormData({
                      ...formData,
                      auto_classify_enabled: enabled,
                      // When enabling, set the date to now if not already set
                      auto_classify_since: enabled && !formData.auto_classify_since
                        ? new Date().toISOString()
                        : enabled
                          ? formData.auto_classify_since
                          : undefined
                    })
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
              </label>
            )}
            {!isEditing && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                formData.auto_classify_enabled ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {formData.auto_classify_enabled ? 'Auto' : 'Manual'}
              </span>
            )}
          </div>

          {/* AI Provider Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              AI Provider
            </label>
            <div className="grid grid-cols-4 gap-2">
              {AI_PROVIDERS.map((provider) => {
                const styles = PROVIDER_STYLES[provider.id]
                const isSelected = getProviderFromModel(formData.model) === provider.id
                return (
                  <button
                    key={provider.id}
                    type="button"
                    disabled={!isEditing}
                    onClick={() => {
                      if (isEditing) {
                        const defaultModel = provider.models.find(m => m.recommended)?.id || provider.models[0].id
                        const newReasoning = getDefaultReasoningForProvider(provider.id, formData.reasoning_effort)
                        setFormData({ ...formData, model: defaultModel, reasoning_effort: newReasoning })
                      }
                    }}
                    className={`relative px-3 py-2.5 rounded-lg border-2 text-center transition-all ${
                      isSelected
                        ? styles.pillActive + ' ' + styles.pillActiveText
                        : styles.pill + ' ' + styles.pillText
                    } ${!isEditing ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${isSelected ? styles.dot : 'bg-gray-300'}`} />
                      <span className="text-sm">{provider.name}</span>
                    </div>
                    {!provider.available && (
                      <span className="block text-[10px] mt-0.5 opacity-60">Coming Soon</span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Model Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Model
            </label>
            {(() => {
              const currentProvider = getProviderInfo(getProviderFromModel(formData.model))
              const styles = PROVIDER_STYLES[currentProvider?.id || 'openai']
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {currentProvider?.models.map((model) => {
                    const isSelected = formData.model === model.id
                    return (
                      <button
                        key={model.id}
                        type="button"
                        disabled={!isEditing}
                        onClick={() => {
                          if (isEditing) {
                            setFormData({ ...formData, model: model.id })
                          }
                        }}
                        className={`relative px-3 py-3 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? styles.ring + ' bg-gray-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        } ${!isEditing ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                              {model.name}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{model.description}</p>
                          </div>
                          {model.recommended && (
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${styles.badge}`}>
                              Top
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <div className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${styles.dot}`} />
                        )}
                      </button>
                    )
                  })}
                </div>
              )
            })()}
            {!getProviderInfo(getProviderFromModel(formData.model))?.available && (
              <p className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                This provider is not yet available. Selection will be saved for when it becomes active.
              </p>
            )}
          </div>

          {/* Reasoning Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Reasoning Level
            </label>
            {(() => {
              const levels = getReasoningLevelsForProvider(getProviderFromModel(formData.model))
              return (
                <div className={`grid gap-2 ${levels.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  {levels.map((level) => {
                    const isSelected = formData.reasoning_effort === level.id
                    const styles = REASONING_STYLES[level.id]
                    return (
                      <button
                        key={level.id}
                        type="button"
                        disabled={!isEditing}
                        onClick={() => {
                          if (isEditing) {
                            setFormData({ ...formData, reasoning_effort: level.id })
                          }
                        }}
                        className={`px-3 py-3 rounded-lg border-2 text-center transition-all ${
                          isSelected
                            ? styles.cardActive + ' ' + styles.textActive
                            : styles.card + ' ' + styles.text
                        } ${!isEditing ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                      >
                        <p className="text-sm font-semibold">{level.name}</p>
                        <p className="text-[11px] mt-0.5 opacity-75">{level.description}</p>
                      </button>
                    )
                  })}
                </div>
              )
            })()}
            <p className="mt-2 text-xs text-gray-500">
              Higher reasoning provides better accuracy but increases response time and cost.
            </p>
          </div>

          {/* Fallback Agent */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fallback Agent
            </label>
            <select
              value={formData.fallback_agent_id || ''}
              onChange={(e) => setFormData({ ...formData, fallback_agent_id: e.target.value || undefined })}
              disabled={!isEditing}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
            >
              <option value="">No Fallback (Skip unclassified emails)</option>
              {agents.filter(a => a.is_active).map(agent => (
                <option key={agent.agent_id} value={agent.agent_id}>
                  {agent.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Agent to handle emails that cannot be classified or are marked as SPAM/UNKNOWN.
            </p>
          </div>

          {/* Custom System Prompt */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                System Prompt
              </label>
              <button
                onClick={() => setShowPromptPreview(!showPromptPreview)}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center space-x-1"
              >
                {showPromptPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                <span>{showPromptPreview ? 'Hide' : 'Show'} {formData.system_prompt ? 'Current' : 'Auto-Generated'} Prompt</span>
              </button>
            </div>

            {/* Status indicator */}
            <div className={`mb-3 p-3 rounded-lg border ${
              formData.system_prompt
                ? 'bg-purple-50 border-purple-200'
                : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-start space-x-2">
                {formData.system_prompt ? (
                  <>
                    <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-purple-900">Using Custom Prompt</p>
                      <p className="text-xs text-purple-700 mt-0.5">
                        You've defined a custom prompt that will be used instead of the auto-generated one.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-blue-900">Using Auto-Generated Prompt</p>
                      <p className="text-xs text-blue-700 mt-0.5">
                        The router automatically generates an optimized prompt based on your active agents and their descriptions.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {showPromptPreview && (
              <div className="mb-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-900">
                    {formData.system_prompt ? 'Current Prompt (Custom)' : 'Auto-Generated Prompt (Active)'}
                  </p>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    formData.system_prompt
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {formData.system_prompt ? 'Custom' : 'Auto'}
                  </span>
                </div>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border border-gray-200 max-h-64 overflow-y-auto">
                  {formData.system_prompt || generatePromptPreview()}
                </pre>
              </div>
            )}

            <textarea
              value={formData.system_prompt || ''}
              onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value || undefined })}
              disabled={!isEditing}
              placeholder="Leave empty to use the auto-generated prompt based on your agents, or enter a custom prompt here..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
              rows={8}
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional: Override the auto-generated prompt with your own custom classification instructions.
            </p>
          </div>
        </div>
      </div>

      {/* Agents Info Card */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-6">
        <div className="flex items-start space-x-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Bot className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-2">How AI Router Works</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                <strong>1.</strong> When an email arrives in AI-Draft-Created, the router analyzes the content.
              </p>
              <p>
                <strong>2.</strong> GPT-5 compares the email against all active agents' descriptions.
              </p>
              <p>
                <strong>3.</strong> The email is automatically assigned to the most appropriate agent.
              </p>
              <p>
                <strong>4.</strong> If no match is found, it routes to the fallback agent (if configured).
              </p>
            </div>
            <div className="mt-4 p-3 bg-white rounded-lg border border-blue-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Active Agents:</span>
                <span className="font-semibold text-gray-900">{agents.filter(a => a.is_active).length}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-gray-600">Total Agents:</span>
                <span className="font-semibold text-gray-900">{agents.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {!isEditing && !error && !loading && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-sm text-green-600">Router configuration is saved and active</span>
        </div>
      )}

      {/* Bulk Re-classification (Super Admin Only) */}
      {isSuperAdmin && (
        <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6 mt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Bulk Re-classification</h3>
              <p className="text-sm text-gray-500">Super Admin — Re-run AI Router on existing classified threads</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Since Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Since Date (optional)</label>
              <input
                type="date"
                value={bulkSinceDate}
                onChange={(e) => setBulkSinceDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={bulkRunning}
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to process all threads</p>
            </div>

            {/* Options */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkForce}
                  onChange={(e) => setBulkForce(e.target.checked)}
                  disabled={bulkRunning}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Force re-classify (overwrite existing)</span>
              </label>
            </div>

            {/* Run Button */}
            <button
              onClick={() => setShowBulkConfirm(true)}
              disabled={bulkRunning}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${bulkRunning ? 'animate-spin' : ''}`} />
              {bulkRunning ? 'Processing...' : 'Run Bulk Re-classify'}
            </button>

            {/* Progress Bar */}
            {bulkRunning && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm text-blue-700">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{bulkProgress?.message || 'Starting bulk reclassify...'}</span>
                  </div>
                  {bulkProgress && bulkProgress.total_found > 0 && (
                    <span className="font-medium">
                      {bulkProgress.reclassified + bulkProgress.skipped + bulkProgress.errors}
                      {' / '}
                      {bulkProgress.total_found}
                    </span>
                  )}
                </div>
                <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-blue-600 rounded-full transition-all duration-300"
                    style={{
                      width: bulkProgress && bulkProgress.total_found > 0
                        ? `${((bulkProgress.reclassified + bulkProgress.skipped + bulkProgress.errors) / bulkProgress.total_found) * 100}%`
                        : '0%'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Results */}
            {bulkResults && !bulkRunning && (
              <div className={`p-4 rounded-lg border ${bulkResults.errors > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Found:</span>
                    <span className="ml-1 font-semibold">{bulkResults.total_found}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Re-classified:</span>
                    <span className="ml-1 font-semibold text-green-700">{bulkResults.reclassified}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Skipped:</span>
                    <span className="ml-1 font-semibold text-gray-700">{bulkResults.skipped}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Errors:</span>
                    <span className="ml-1 font-semibold text-red-700">{bulkResults.errors}</span>
                  </div>
                </div>
                {bulkResults.error_details.length > 0 && (
                  <div className="mt-3 text-xs text-red-600 space-y-1">
                    {bulkResults.error_details.map((e, i) => (
                      <div key={i}>{e}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Re-classify Confirmation Modal */}
      <ConfirmModal
        open={showBulkConfirm}
        onCancel={() => setShowBulkConfirm(false)}
        onConfirm={handleBulkReclassify}
        title="Bulk Re-classification"
        icon={
          <div className="p-2 bg-blue-100 rounded-lg">
            <Router className="w-5 h-5 text-blue-600" />
          </div>
        }
        description={
          <div className="space-y-3">
            <p>
              This will re-classify <strong>{bulkForce ? 'all' : 'only unclassified'}</strong> threads
              {bulkSinceDate ? <> since <strong>{bulkSinceDate}</strong></> : <> <span className="text-gray-500">(all time)</span></>}.
            </p>
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-800">
              This operation may take a while and will consume AI API credits.
            </div>
          </div>
        }
        confirmLabel="Run Re-classification"
        confirmColor="bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
        loading={bulkRunning}
      />
    </div>
  )
}
